import { isNull } from 'lodash';
import React from 'react';
import {
  ComposedChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  ZAxis,
  Scatter,
  CartesianGrid,
  LabelList,
  Tooltip,
} from 'recharts';
import { useData } from '../../contexts/useData';
import * as d3 from 'd3';

interface VisualProps {
  id: number;
  fieldName: string;
  type: string; // "main" or "benchmark", only 1 can have main
}

const CustomizedShape = (props: any) => {
  const { cx, cy, fillColor, barSize, payload } = props;

  let height = barSize ? barSize + 12 : props.yAxis.height;
  let width = 2;
  let svgOut = (
    <svg
      overflow="visible"
      x={cx - width / 2}
      y={cy - height / 2}
      fill={fillColor}
      fillOpacity={1}
    >
      <path d={`M0 0 L0 ${height} L${width} ${height} L${width} 0 Z`} />
    </svg>
  );

  return svgOut;
};

const showOrHideScatterPoint = (props: any, fieldName: string) => {
  // const { data, idField, visualProps } = props;

  return props.data[0][fieldName] === undefined ||
    isNull(props.data[0][fieldName])
    ? true
    : false;
};

const CustomLabel = (props: any, data: any, colors: any, locale: any) => {
  const value =
    data.d3format !== undefined
      ? locale.format(data.d3format)(props.value)
      : props.value;

  return (
    <g>
      <rect
        x={props.viewBox.x - 10}
        y={props.viewBox.y - 18}
        rx={6}
        // fill="#FFF"
        // stroke="#DDD"
        fill={'transparent'} /*{colors[0]}*/
        fillOpacity={0.9}
        stroke="transparent" /*{colors[0]}*/
        strokeOpacity={0.5}
        width={40}
        height={15}
      />
      <text
        x={props.viewBox.x}
        y={props.viewBox.y}
        fill="#111"
        // fill="#fff"
        dx={8}
        dy={-10}
        textAnchor="middle"
      >
        {value}
      </text>
    </g>
  );
};

const CustomLabelBench = (props: any, data: any, colors: any, locale: any) => {
  const value =
    data.d3format !== undefined
      ? locale.format(data.d3format)(props.value)
      : props.value;
  return (
    <g>
      {/* <rect
        x={props.viewBox.x - 10}
        y={props.viewBox.y + 23}
        rx={6}
        // fill={colors[1]}
        fill="transparent"
        stroke={colors[1]}
        strokeOpacity={0.5}
        width={40}
        height={15}
      /> */}
      <text
        x={props.viewBox.x}
        y={props.viewBox.y}
        fill="#111"
        // fill="#fff"
        dx={8}
        dy={35}
        textAnchor="middle"
      >
        {value}
      </text>
    </g>
  );
};

const CustomLabelInteractieve = (
  props: any,
  data: any,
  colors: any,
  locale: any,
) => {
  const value =
    data.d3format !== undefined
      ? locale.format(data.d3format)(props.value)
      : props.value;

  return (
    <g>
      <rect
        x={props.viewBox.x - 10}
        y={props.viewBox.y - 18}
        rx={6}
        fill={'transparent'} /*{colors[0]}*/
        fillOpacity={0.9}
        stroke="transparent" /*{colors[0]}*/
        strokeOpacity={0.5}
        width={40}
        height={15}
      />
      <text
        x={props.viewBox.x}
        y={props.viewBox.y}
        fill="#111"
        dx={data?.d3format.includes('%') ? 10 : 5}
        dy={-8}
        textAnchor="middle"
      >
        {value}
      </text>
    </g>
  );
};

const CustomLabelBenchInteractieve = (
  props: any,
  data: any,
  colors: any,
  locale: any,
) => {
  const value =
    data.d3format !== undefined
      ? locale.format(data.d3format)(props.value)
      : props.value;
  return (
    <g>
      <text
        x={props.viewBox.x}
        y={props.viewBox.y}
        fill="#111"
        // fill="#fff"
        dx={data?.d3format.includes('%') ? 10 : 5}
        dy={28}
        textAnchor="middle"
      >
        {value}
      </text>
    </g>
  );
};

export const ScatterChartOneDimensionalSmall = (props: {
  data: any[];
  idField: string;
  visualProps: VisualProps[];
  density?: string;
  isMobile?: boolean;
}) => {
  const { data, idField, visualProps, density, isMobile } = props;

  const { vregColors, locale, selectedGemeente, selectedBenchmarks } =
    useData();

  const colors = vregColors;

  const valueMin = data[0] !== undefined ? data[0].valueMin : 'dataMin';
  const valueMax = data[0] !== undefined ? data[0].valueMax : 'dataMax';

  const mainRow: any =
    visualProps && visualProps.find((d) => d.type === 'main');

  const targetRows: any[] =
    visualProps && visualProps.filter((d) => d.type === 'benchmark');

  const minRow: any = data[0] !== undefined ? data[0] : [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const titel = payload[0].payload
        ? payload[0].payload.label
        : '(ontbrekende titel)';

      const definitie = payload[0].payload.detail
        ? payload[0].payload.detail.definitie
        : '(ontbrekende definitie)';

      const payloadGemeente = payload.find(
        (d: any) => d.name === selectedGemeente.ruimte,
      );
      const payloadBench = payload.find(
        (d: any) => d.name === selectedBenchmarks[0].ruimte,
      );
      const payloadVG = payload.find(
        (d: any) => d.name === 'valueVlaamsGewest',
      );

      return (
        <div
          style={{
            fontSize: '14px',
            background: '#fff',
            border: '1px solid black',
            padding: '8px',
            maxWidth: '300px',
          }}
        >
          {/* <p style={{ fontWeight: 'bold', marginBottom: '6px' }}>{titel}</p> */}
          {/* <p>{definitie}</p> */}
          {payloadGemeente && (
            <p style={{ marginBottom: '2px' }}>
              <svg
                width="10"
                height="10"
                viewBox="0 0 32 32"
                style={{
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  marginRight: '4px',
                }}
              >
                <title></title>
                <desc></desc>
                <path
                  fill={colors[0]}
                  cx="16"
                  cy="16"
                  transform="translate(16, 16)"
                  d="M16,0A16,16,0,1,1,-16,0A16,16,0,1,1,16,0"
                ></path>
              </svg>
              <span
                style={{ fontWeight: 'bold' }}
              >{`${payloadGemeente.name}:`}</span>{' '}
              {`${locale.format(payloadGemeente.payload.d3format)(
                payloadGemeente.value,
              )}`}
            </p>
          )}
          {payloadBench && (
            <p style={{ marginBottom: '2px' }}>
              <svg
                width="10"
                height="10"
                viewBox="0 0 32 32"
                style={{
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  marginRight: '4px',
                }}
              >
                <title></title>
                <desc></desc>
                <path
                  fill={'transparent'}
                  stroke={colors[1]}
                  strokeWidth={5}
                  transform="translate(16, 16)"
                  d="M14,0A14,14,0,1,1,-14,0A14,14,0,1,1,14,0"
                ></path>
              </svg>
              <span
                style={{ fontWeight: 'bold' }}
              >{`${payloadBench.name}:`}</span>{' '}
              {`${locale.format(payloadBench.payload.d3format)(
                payloadBench.value,
              )}`}
            </p>
          )}

          {payloadVG && (
            <p>
              <svg
                width="10"
                height="10"
                viewBox="0 0 32 32"
                style={{
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  marginRight: '4px',
                }}
              >
                <title></title>
                <desc></desc>
                <line
                  x1="15"
                  x2="15"
                  y1="0"
                  y2="32"
                  stroke={colors[2]}
                  stroke-width="8"
                />
              </svg>
              <span style={{ fontWeight: 'bold' }}>{`Vlaams Gewest:`}</span>{' '}
              {`${locale.format(payloadVG.payload.d3format)(payloadVG.value)}`}
            </p>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
      className="cartesianNoOuterLines"
    >
      <ComposedChart
        data={data}
        layout="vertical"
        margin={{ left: 64, right: 64 }}
        style={{ cursor: 'pointer' }}
      >
        {/* <defs>
          {visualProps.map((item, index) => (
            <linearGradient
              id={`colorUv${index}`}
              x1="-200%"
              y1="0"
              x2="100%"
              y2="0"
              spreadMethod="reflect"
            >
              <stop offset="0" stopColor="#FFF"></stop>
              <stop offset="1" stopColor={colors[index]}></stop>
            </linearGradient>
          ))}
        </defs> */}
        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<CustomTooltip />}
        />
        <CartesianGrid
          fill="#EEE"
          fillOpacity={0}
          stroke={'#DDD'}
          strokeOpacity={0.6}
        ></CartesianGrid>
        <XAxis
          type="number"
          hide={true}
          domain={[valueMin, valueMax]}
          tickCount={1}
        />
        <YAxis type="category" dataKey={idField} hide={true} />
        <ZAxis range={[200, 1000]} dataKey="size" />

        {/* // Render the bubbles for the benchmarks */}
        {targetRows.reverse().map((item, index) => (
          <Scatter
            key={item.id}
            dataKey={item.fieldName}
            shape={'circle'}
            fill={'transparent'}
            stroke={targetRows.length === index + 1 ? colors[1] : colors[2]}
            strokeOpacity={1}
            strokeWidth={3}
            hide={showOrHideScatterPoint(props, item.fieldName)}
            isAnimationActive={false}
          >
            <LabelList
              dataKey={item.fieldName}
              content={(props) => {
                if (index === targetRows.length - 1) {
                  return CustomLabelBench(props, data[0], colors, locale);
                }
              }}
            ></LabelList>
          </Scatter>
        ))}

        {/* // Render the line in the center of the dot for the city */}
        {/* <Scatter
          key={mainRow.fieldName}
          dataKey={mainRow.fieldName}
          shape={<CustomizedShape fillColor={colors[0]} barSize={10} />}
          // shape={'dot'}
          fill={colors[0]}
          fillOpacity={1}
          stroke={colors[0]}
          strokeOpacity={1}
          strokeWidth={1}
          hide={showOrHideScatterPoint(props, mainRow.fieldName)}
        /> */}

        {/* // Render the bubble for the selected city */}
        <Scatter
          key={mainRow.id}
          dataKey={mainRow.fieldName}
          // shape={
          //   <CustomizedShape barSize={10} fillColor={colors[index + 1]} />
          // }
          shape="circle"
          fill={colors[0]}
          // fillOpacity={1}
          // fill={'transparent'}
          stroke={colors[0]}
          strokeOpacity={1}
          strokeWidth={2}
          hide={showOrHideScatterPoint(props, mainRow.fieldName)}
          isAnimationActive={false}
        >
          <LabelList
            dataKey={mainRow.fieldName}
            content={(props) => CustomLabel(props, data[0], colors, locale)}
          ></LabelList>
        </Scatter>

        {data[0].valueVlaamsGewest && (
          <Scatter
            key={'valueVlaamsGewest'}
            dataKey={'valueVlaamsGewest'}
            shape={<CustomizedShape fillColor={colors[2]} barSize={10} />}
            // shape="circle"
            // fill={colors[0]}
            // fillOpacity={1}
            // stroke={colors[0]}
            // strokeOpacity={1}
            // strokeWidth={1}
          />
        )}

        <Scatter
          key={'valuemin'}
          dataKey={'valueMin'}
          shape={<CustomizedShape fillColor={'#ddd'} barSize={10} />}
        />
        <text
          x="0"
          y="38%"
          dy={8}
          dx={20}
          style={{ fontSize: '1.2rem', fontWeight: 'bold', fill: '#333' }}
          width={60}
          textAnchor="left"
        >
          Min
        </text>
        <text
          x="0"
          y="45%"
          dx={30}
          dy={16}
          style={{ fontSize: isMobile ? '1.2rem' : '1.4rem' }}
          width={60}
          textAnchor="middle"
        >
          {locale.format(data[0].d3format)(valueMin)}
        </text>

        <Scatter
          key={'valuemax'}
          dataKey={'valueMax'}
          shape={<CustomizedShape fillColor={'#ddd'} barSize={10} />}
          // // shape="circle"
          // fill={colors[0]}
          // fillOpacity={1}
          // stroke={colors[0]}
          // strokeOpacity={1}
          // strokeWidth={1}
        />
        <text
          x="100%"
          y="38%"
          dy={8}
          dx={-44}
          style={{ fontSize: '1.2rem', fontWeight: 'bold', fill: '#333' }}
          width={60}
          textAnchor="left"
        >
          Max
        </text>
        <text
          x="100%"
          y="45%"
          dx={-31}
          dy={16}
          style={{ fontSize: isMobile ? '1.2rem' : '1.4rem' }}
          width={60}
          textAnchor="middle"
        >
          {locale.format(data[0].d3format)(valueMax)}
        </text>
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export const ScatterChartOneDimensionalInteractieveTool = (props: {
  data: any[];
  idField: string;
  visualProps: VisualProps[];
  mainOnly?: boolean;
}) => {
  const { data, idField, visualProps, mainOnly } = props;
  const {
    benchColors,
    gemeenteColor,
    vgColor,
    colors,
    locale,
    selectedGemeente,
    selectedBenchmarks,
  } = useData();

  const valueMin = data[0] !== undefined ? data[0].valueMin : 'dataMin';
  const valueMax = data[0] !== undefined ? data[0].valueMax : 'dataMax';

  const mainRow: any =
    visualProps && visualProps.find((d) => d.type === 'main');

  const targetRows: any[] =
    visualProps && visualProps.filter((d) => d.type === 'benchmark');

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // const titel = payload[0].payload
      //   ? payload[0].payload.label
      //   : '(ontbrekende titel)';

      // const definitie = payload[0].payload.detail
      //   ? payload[0].payload.detail.definitie
      //   : '(ontbrekende definitie)';

      const payloadGemeente = payload.find(
        (d: any) => d.name === selectedGemeente.ruimte,
      );

      let payloadVG = payload.find((d: any) => d.name === 'valueVlaamsGewest');

      if (mainOnly) {
        payloadVG = undefined;
      }

      return (
        <div
          style={{
            fontSize: '14px',
            background: '#fff',
            border: '1px solid black',
            color: '#111',
            padding: '6px',
            maxWidth: '300px',
          }}
        >
          {/* <p style={{ fontWeight: 'bold', marginBottom: '6px' }}>{titel}</p> */}
          {/* <p>{definitie}</p> */}
          {payloadGemeente && (
            <p style={{ marginBottom: '2px' }}>
              <svg
                width="10"
                height="10"
                viewBox="0 0 32 32"
                style={{
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  marginRight: '4px',
                }}
              >
                <title></title>
                <desc></desc>
                <path
                  fill={gemeenteColor}
                  cx="16"
                  cy="16"
                  transform="translate(16, 16)"
                  d="M16,0A16,16,0,1,1,-16,0A16,16,0,1,1,16,0"
                ></path>
              </svg>
              <span
                style={{ fontWeight: 'bold' }}
              >{`${payloadGemeente.name}:`}</span>{' '}
              {`${locale.format(payloadGemeente.payload.d3format)(
                payloadGemeente.value,
              )}`}
            </p>
          )}

          {payloadVG && (
            <p>
              <svg
                width="10"
                height="10"
                viewBox="0 0 32 32"
                style={{
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  marginRight: '4px',
                }}
              >
                <title></title>
                <desc></desc>
                <line
                  x1="15"
                  x2="15"
                  y1="0"
                  y2="32"
                  stroke={vgColor}
                  stroke-width="8"
                />
              </svg>
              <span style={{ fontWeight: 'bold' }}>{`Vlaams Gewest:`}</span>{' '}
              {`${locale.format(payloadVG.payload.d3format)(payloadVG.value)}`}
            </p>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div style={{ width: '100%', height: '100%', padding: 2 }}>
      <ResponsiveContainer
        width="100%"
        height="100%"
        className="cartesianNoOuterLines"
      >
        <ComposedChart
          data={data}
          layout="vertical"
          margin={{ left: 104, right: 104 }}
          style={{
            cursor: 'pointer',
            // backgroundColor: 'white',
            // border: '1px solid #eee',
          }}
        >
          {/* <Tooltip
            cursor={{ strokeDasharray: '3 3' }}
            content={<CustomTooltip />}
          /> */}
          <CartesianGrid
            fill="#EEE"
            fillOpacity={0}
            stroke={'#ccc'}
            strokeOpacity={1}
            strokeWidth={1}
          ></CartesianGrid>
          <XAxis
            type="number"
            hide={true}
            domain={[valueMin, valueMax]}
            tickCount={1}
          />
          <YAxis type="category" dataKey={idField} hide={true} />
          <ZAxis range={[100, 1000]} dataKey="size" />

          {/* // Render the lines in the center of the dots for the benchmarks */}
          {/* {targetRows.toReversed().map((item, index) => (
          <Scatter
            key={item.fieldName}
            dataKey={item.fieldName}
            shape={
              <CustomizedShape
                fillColor={
                  targetRows.length === index + 1
                    ? darken(colors[1], 0.2)
                    : darken(colors[2], 0.2)
                }
              />
            }
            hide={showOrHideScatterPoint(props, item.fieldName)}
          />
        ))} */}

          {/* // Render the bubbles for the benchmarks */}
          {targetRows.map((item, index) => (
            <Scatter
              key={item.id}
              dataKey={item.fieldName}
              shape={'circle'}
              fill={'transparent'}
              // stroke={targetRows.length === index + 1 ? colors[1] : colors[5]}
              stroke={benchColors[index]}
              strokeOpacity={1}
              strokeWidth={2}
              hide={showOrHideScatterPoint(props, item.fieldName)}
              //isAnimationActive={false}
            >
              <LabelList
                dataKey={item.fieldName}
                content={(props) => {
                  if (index === 0) {
                    return CustomLabelBenchInteractieve(
                      props,
                      data[0],
                      colors,
                      locale,
                    );
                  }
                }}
              ></LabelList>
            </Scatter>
          ))}

          {/* // Render the line in the center of the dot for the city */}
          {/* <Scatter
          key={mainRow.fieldName}
          dataKey={mainRow.fieldName}
          shape={<CustomizedShape fillColor={colors[0]} barSize={10} />}
          // shape={'dot'}
          fill={colors[0]}
          fillOpacity={1}
          stroke={colors[0]}
          strokeOpacity={1}
          strokeWidth={1}
          hide={showOrHideScatterPoint(props, mainRow.fieldName)}
        /> */}

          {/* // Render the bubble for the selected city */}
          <Scatter
            key={mainRow.id}
            dataKey={mainRow.fieldName}
            // shape={
            //   <CustomizedShape barSize={10} fillColor={colors[index + 1]} />
            // }
            shape="circle"
            fill={gemeenteColor}
            // fillOpacity={1}
            // fill={'transparent'}
            stroke={gemeenteColor}
            strokeOpacity={1}
            strokeWidth={2}
            hide={showOrHideScatterPoint(props, mainRow.fieldName)}
            //  isAnimationActive={false}
          >
            <LabelList
              dataKey={mainRow.fieldName}
              content={(props) =>
                CustomLabelInteractieve(props, data[0], colors, locale)
              }
            ></LabelList>
          </Scatter>

          {!mainOnly &&
            data.length > 0 &&
            data[0].valueVlaamsGewest !== null && (
              <Scatter
                key={'valueVlaamsGewest'}
                dataKey={'valueVlaamsGewest'}
                shape={<CustomizedShape fillColor={vgColor} barSize={10} />}
                // shape="circle"
                // fill={colors[0]}
                // fillOpacity={1}
                // stroke={colors[0]}
                // strokeOpacity={1}
                // strokeWidth={1}
                //isAnimationActive={false}
              />
            )}

          <Scatter
            key={'valuemin'}
            dataKey={'valueMin'}
            shape={<CustomizedShape fillColor={'#ddd'} barSize={10} />}
            // shape="circle"
            // fill={colors[0]}
            // fillOpacity={1}
            // stroke={colors[0]}
            // strokeOpacity={1}
            // strokeWidth={1}
            //isAnimationActive={false}
          />
          <text
            x="0"
            y="38%"
            dy={6}
            dx={40}
            style={{ fontSize: '1.2rem', fontWeight: 'bold', fill: '#333' }}
            width={60}
            textAnchor="left"
          >
            Min
          </text>
          <text
            x="0"
            y="45%"
            dx={50}
            dy={16}
            style={{ fontSize: '1.4rem' }}
            width={60}
            textAnchor="middle"
          >
            {locale.format(data[0].d3format)(valueMin)}
          </text>

          <Scatter
            key={'valuemax'}
            dataKey={'valueMax'}
            shape={<CustomizedShape fillColor={'#ddd'} barSize={10} />}
            // shape="circle"
            // fill={colors[0]}
            // fillOpacity={1}
            // stroke={colors[0]}
            // strokeOpacity={1}
            // strokeWidth={1}
            //isAnimationActive={false}
          />
          <text
            x="100%"
            y="38%"
            dy={6}
            dx={-64}
            style={{ fontSize: '1.2rem', fontWeight: 'bold', fill: '#333' }}
            width={60}
            textAnchor="left"
          >
            Max
          </text>
          <text
            x="100%"
            y="45%"
            dx={-52}
            dy={16}
            style={{ fontSize: '1.4rem' }}
            width={60}
            textAnchor="middle"
          >
            {locale.format(data[0].d3format)(valueMax)}
          </text>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export const ScatterChartOneDimensionalSmallMobile = (props: {
  data: any[];
  idField: string;
  visualProps: VisualProps[];
  density?: string;
}) => {
  const { data, idField, visualProps, density } = props;
  const { vregColors, locale, selectedGemeente, selectedBenchmarks } =
    useData();

  const colors = vregColors;

  const valueMin = data[0] !== undefined ? data[0].valueMin : 'dataMin';
  const valueMax = data[0] !== undefined ? data[0].valueMax : 'dataMax';

  const mainRow: any =
    visualProps && visualProps.find((d) => d.type === 'main');

  const targetRows: any[] =
    visualProps && visualProps.filter((d) => d.type === 'benchmark');

  const minRow: any = data[0] !== undefined ? data[0] : [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const titel = payload[0].payload
        ? payload[0].payload.label
        : '(ontbrekende titel)';

      const definitie = payload[0].payload.detail
        ? payload[0].payload.detail.definitie
        : '(ontbrekende definitie)';

      const payloadGemeente = payload.find(
        (d: any) => d.name === selectedGemeente.ruimte,
      );
      const payloadBench = payload.find(
        (d: any) => d.name === selectedBenchmarks[0].ruimte,
      );
      const payloadVG = payload.find(
        (d: any) => d.name === 'valueVlaamsGewest',
      );

      return (
        <div
          style={{
            fontSize: '11px',
            background: '#fff',
            border: '1px solid black',
            padding: '8px',
            maxWidth: '300px',
          }}
        >
          {/* <p style={{ fontWeight: 'bold', marginBottom: '6px' }}>{titel}</p> */}
          {/* <p>{definitie}</p> */}
          {payloadGemeente && (
            <p style={{ marginBottom: '2px' }}>
              <svg
                width="10"
                height="10"
                viewBox="0 0 32 32"
                style={{
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  marginRight: '4px',
                }}
              >
                <title></title>
                <desc></desc>
                <path
                  fill={colors[0]}
                  cx="16"
                  cy="16"
                  transform="translate(16, 16)"
                  d="M16,0A16,16,0,1,1,-16,0A16,16,0,1,1,16,0"
                ></path>
              </svg>
              <span
                style={{ fontWeight: 'bold' }}
              >{`${payloadGemeente.name}:`}</span>{' '}
              {`${locale.format(payloadGemeente.payload.d3format)(
                payloadGemeente.value,
              )}`}
            </p>
          )}
          {payloadBench && (
            <p style={{ marginBottom: '2px' }}>
              <svg
                width="10"
                height="10"
                viewBox="0 0 32 32"
                style={{
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  marginRight: '4px',
                }}
              >
                <title></title>
                <desc></desc>
                <path
                  fill={'transparent'}
                  stroke={colors[1]}
                  strokeWidth={5}
                  transform="translate(16, 16)"
                  d="M14,0A14,14,0,1,1,-14,0A14,14,0,1,1,14,0"
                ></path>
              </svg>
              <span
                style={{ fontWeight: 'bold' }}
              >{`${payloadBench.name}:`}</span>{' '}
              {`${locale.format(payloadBench.payload.d3format)(
                payloadBench.value,
              )}`}
            </p>
          )}

          {payloadVG && (
            <p>
              <svg
                width="10"
                height="10"
                viewBox="0 0 32 32"
                style={{
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  marginRight: '4px',
                }}
              >
                <title></title>
                <desc></desc>
                <line
                  x1="15"
                  x2="15"
                  y1="0"
                  y2="32"
                  stroke={colors[2]}
                  stroke-width="8"
                />
              </svg>
              <span style={{ fontWeight: 'bold' }}>{`Vlaams Gewest:`}</span>{' '}
              {`${locale.format(payloadVG.payload.d3format)(payloadVG.value)}`}
            </p>
          )}
        </div>
      );
    }

    return null;
  };

  const CustomLabelBench = (
    props: any,
    data: any,
    colors: any,
    locale: any,
  ) => {
    const value =
      data.d3format !== undefined
        ? locale.format(data.d3format)(props.value)
        : props.value;
    return (
      <g>
        <text
          x={props.viewBox.x}
          y={props.viewBox.y}
          fill="#111"
          fontSize="11px"
          dx={6}
          dy={28}
          textAnchor="middle"
        >
          {value}
        </text>
      </g>
    );
  };

  const CustomLabel = (props: any, data: any, colors: any, locale: any) => {
    const value =
      data.d3format !== undefined
        ? locale.format(data.d3format)(props.value)
        : props.value;

    return (
      <g>
        <rect
          x={props.viewBox.x - 10}
          y={props.viewBox.y - 18}
          rx={6}
          // fill="#FFF"
          // stroke="#DDD"
          fill={'transparent'} /*{colors[0]}*/
          fillOpacity={0.9}
          stroke="transparent" /*{colors[0]}*/
          strokeOpacity={0.5}
          width={40}
          height={15}
        />
        <text
          x={props.viewBox.x}
          y={props.viewBox.y}
          fill="#111"
          // fill="#fff"
          dx={6}
          dy={-8}
          textAnchor="middle"
        >
          {value}
        </text>
      </g>
    );
  };

  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
      className="cartesianNoOuterLines"
    >
      <ComposedChart
        data={data}
        layout="vertical"
        margin={{ left: 64, right: 64 }}
        style={{ cursor: 'pointer' }}
      >
        {/* <defs>
          {visualProps.map((item, index) => (
            <linearGradient
              id={`colorUv${index}`}
              x1="-200%"
              y1="0"
              x2="100%"
              y2="0"
              spreadMethod="reflect"
            >
              <stop offset="0" stopColor="#FFF"></stop>
              <stop offset="1" stopColor={colors[index]}></stop>
            </linearGradient>
          ))}
        </defs> */}
        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          content={<CustomTooltip />}
          // formatter={(value) =>
          //   data[0] && data[0].d3format
          //     ? locale.format(data[0].d3format)(value)
          //     : value
          // }
        />
        <CartesianGrid
          fill="#EEE"
          fillOpacity={0}
          stroke={'#DDD'}
          strokeOpacity={0.6}
        ></CartesianGrid>
        <XAxis
          type="number"
          hide={true}
          domain={[valueMin, valueMax]}
          tickCount={1}
        />
        <YAxis type="category" dataKey={idField} hide={true} />
        <ZAxis range={[100, 1000]} dataKey="size" />

        {/* // Render the bubbles for the benchmarks */}
        {targetRows.reverse().map((item, index) => (
          <Scatter
            key={item.id}
            dataKey={item.fieldName}
            shape={'circle'}
            fill={'transparent'}
            stroke={targetRows.length === index + 1 ? colors[1] : colors[2]}
            strokeOpacity={1}
            strokeWidth={1}
            hide={showOrHideScatterPoint(props, item.fieldName)}
            // isAnimationActive={false}
          >
            <LabelList
              dataKey={item.fieldName}
              content={(props) => {
                if (index === targetRows.length - 1) {
                  return CustomLabelBench(props, data[0], colors, locale);
                }
              }}
            ></LabelList>
          </Scatter>
        ))}

        {/* // Render the bubble for the selected city */}
        <Scatter
          key={mainRow.id}
          dataKey={mainRow.fieldName}
          // shape={
          //   <CustomizedShape barSize={10} fillColor={colors[index + 1]} />
          // }
          shape="circle"
          fill={colors[0]}
          // fillOpacity={1}
          // fill={'transparent'}
          stroke={colors[0]}
          strokeOpacity={1}
          strokeWidth={2}
          hide={showOrHideScatterPoint(props, mainRow.fieldName)}
          // isAnimationActive={false}
        >
          <LabelList
            dataKey={mainRow.fieldName}
            content={(props) => CustomLabel(props, data[0], colors, locale)}
          ></LabelList>
        </Scatter>

        <Scatter
          key={'valueVlaamsGewest'}
          dataKey={'valueVlaamsGewest'}
          shape={<CustomizedShape fillColor={colors[2]} barSize={8} />}
          // shape="circle"
          fill={colors[0]}
          fillOpacity={1}
          stroke={colors[0]}
          strokeOpacity={1}
          strokeWidth={1}
        />

        <Scatter
          key={'valuemin'}
          dataKey={'valueMin'}
          shape={<CustomizedShape fillColor={'#ddd'} barSize={4} />}
          // shape="circle"
          fill={colors[0]}
          fillOpacity={1}
          stroke={colors[0]}
          strokeOpacity={1}
          strokeWidth={1}
        />
        <text
          x="0"
          y="38%"
          dy={8}
          dx={28}
          style={{ fontSize: '11px', fontWeight: 'bold', fill: '#333' }}
          width={60}
          textAnchor="left"
        >
          Min
        </text>
        <text
          x="0"
          y="45%"
          dx={38}
          dy={16}
          style={{ fontSize: '11px' }}
          width={60}
          textAnchor="middle"
        >
          {locale.format(data[0].d3format)(valueMin)}
        </text>

        <Scatter
          key={'valuemax'}
          dataKey={'valueMax'}
          shape={<CustomizedShape fillColor={'#ddd'} barSize={4} />}
          // shape="circle"
          fill={colors[0]}
          fillOpacity={1}
          stroke={colors[0]}
          strokeOpacity={1}
          strokeWidth={1}
        />
        <text
          x="100%"
          y="38%"
          dy={8}
          dx={-44}
          style={{ fontSize: '11px', fontWeight: 'bold', fill: '#333' }}
          width={60}
          textAnchor="left"
        >
          Max
        </text>
        <text
          x="100%"
          y="45%"
          dx={-34}
          dy={16}
          style={{ fontSize: '11px' }}
          width={60}
          textAnchor="middle"
        >
          {locale.format(data[0].d3format)(valueMax)}
        </text>
      </ComposedChart>
    </ResponsiveContainer>
  );
};
